// src/pages/SignupPage.js
//
// Provider-first signup. User taps "Continue with Google / Apple / Email",
// optionally completes OAuth or sets a password, then verifies a phone via
// Firebase Phone Auth. The phone credential is linked to the Firebase user
// (one UID, multiple credentials) and a Polyp Users row is created — or, if
// the phone matches a shadow account, the existing FK migration runs.
//
// State machine:
//   entry  → tap provider (Google/Apple) signs in immediately
//          → tap email reveals password fields (Firebase user created on phone step)
//   phone  → enter phone → /auth/lookup branches:
//              realUserExists → block, route to /login
//              shadowFound    → store shadowUserId, send Firebase OTP
//              none           → send Firebase OTP
//   code   → enter OTP → Firebase verifies → link to current Firebase user
//              (for email path, createUserWithEmailAndPassword here first)
//          → if shadow, call /auth/verify-claim (Authorization header proves
//            the verified phone identity, FK migration runs)
//          → otherwise advance to details
//   details → collect remaining profile fields (firstName, lastName, address,
//             dob; email if not from provider) → POST /users → navigate

import React, { useEffect, useRef, useState } from 'react';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  AsYouType,
  formatIncompletePhoneNumber,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';
import { auth } from '../firebase';
import {
  signInWithGoogle,
  signInWithApple,
  consumePendingRedirectResult,
  startPhoneVerification,
  linkPhoneToCurrentUser,
  resetRecaptcha,
} from '../services/auth.providers';
import api from '../services/api.service';
import { loadMapsScript } from '../services/googleMaps';
import { useAuth } from '../components/shared/AuthContext';
import { useRole } from '../components/shared/RoleContext';
import './SignupPage.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL?.replace(/\/$/, '');
const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
const DOT_COLORS = ['#10b981', '#f59e0b', '#ef4444'];
const STEP = { ENTRY: 'entry', PHONE: 'phone', CODE: 'code', DETAILS: 'details' };

const WORDWALL_PHRASE = 'the ambitious, hustlers, innovators, dreamers, coaches, creators, builders, champions, leaders, trailblazers, go-getters, visionaries, game-changers, risk-takers, grinders, athletes, mentors, pioneers, self-starters, doers, warriors, strivers, competitors, underdogs, MVPs, trainers, founders, disruptors, the relentless, the passionate, the driven, the fearless, the dedicated, the unstoppable, ';
// Tile the phrase enough times to fill `.auth-wordwall` (which is 140% of the
// containing block thanks to `inset: -20%`) at any reasonable viewport size.
// Tuned against the smaller font (`clamp(40px, 6vh, 80px)`) — at 100px font 3x
// was enough; at 50px font we need ~4x more words to cover the same area.
const WORDWALL_TEXT = WORDWALL_PHRASE.repeat(12);

async function authedFetch(path, { body, method = 'POST' } = {}) {
  const user = auth.currentUser;
  const token = user ? await user.getIdToken(true) : null;
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch {}
  if (!res.ok) {
    const msg = (data && (data.error || data.message)) || res.statusText;
    const e = new Error(msg); e.status = res.status; e.payload = data; throw e;
  }
  return data ?? {};
}

// As-you-type formatter. Default country is US so a bare 10-digit US number
// formats as (555) 123-4567; if the user types a `+` prefix, libphonenumber
// switches to the relevant region's format (e.g. +44 7700 900123).
const formatPhone = (val) => new AsYouType('US').input(String(val || ''));

// Convert what's currently in the input to canonical E.164. Returns '' when
// the input isn't parseable yet — callers should treat empty as "not ready".
const phoneToE164 = (val) => {
  const parsed = parsePhoneNumberFromString(String(val || ''), 'US');
  return parsed?.isValid() ? parsed.number : '';
};

const isPhoneInputValid = (val) => isValidPhoneNumber(String(val || ''), 'US');

export default function SignupPage() {
  const nav = useNavigate();
  const { state } = useLocation() || {};
  const { refreshUserInfo } = useAuth();
  const { refresh: refreshRole } = useRole();

  const [step, setStep] = useState(STEP.ENTRY);
  const [method, setMethod] = useState(null); // 'google' | 'apple' | 'email'
  const [providerLoading, setProviderLoading] = useState(null);

  const [phone, setPhone] = useState('+1 ');
  const [code, setCode] = useState('');
  const [shadowUserId, setShadowUserId] = useState(null);
  const [maskedPhone, setMaskedPhone] = useState('');
  const [accountExists, setAccountExists] = useState(false);
  const [confirmation, setConfirmation] = useState(null); // Firebase ConfirmationResult

  // REMOVED-CRM-ROUTES (2026-08-23): default was 'client' — restore with the
  // Parent card below when the CRM comes back.
  // Always "official", deliberately NOT "manager": a web signup is then opened
  // in the App Store PW Manager app, and 1.19.0 routes on accountType — any
  // value it doesn't know lands on its retired-account screen. "manager"
  // arrives with the backend backfill (backend/CLEANUP-LEDGER.md).
  const accountType = 'official';
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [address, setAddress] = useState('');
  const [dob, setDob] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(state?.error || '');
  const [success, setSuccess] = useState('');
  const [bgColor] = useState(() => DOT_COLORS[Math.floor(Math.random() * DOT_COLORS.length)]);

  const navTimerRef = useRef(null);
  const addressContainerRef = useRef(null);
  const phoneE164 = phoneToE164(phone);
  const phoneIsValid = isPhoneInputValid(phone);
  const phoneDisplay = phoneE164
    ? formatIncompletePhoneNumber(phoneE164)
    : phone;

  // 18+ gate. String comparison on YYYY-MM-DD is valid date ordering, so
  // `dob > maxDob` ↔ user is under 18.
  const maxDob = (() => {
    const t = new Date();
    const c = new Date(t.getFullYear() - 18, t.getMonth(), t.getDate());
    return `${c.getFullYear()}-${String(c.getMonth() + 1).padStart(2, '0')}-${String(c.getDate()).padStart(2, '0')}`;
  })();
  const dobUnder18 = !!dob && dob > maxDob;

  const passwordChecks = {
    length: password.length >= 8,
    upper: /[A-Z]/.test(password),
    lower: /[a-z]/.test(password),
    number: /\d/.test(password),
  };
  const passwordValid = Object.values(passwordChecks).every(Boolean);

  useEffect(() => () => clearTimeout(navTimerRef.current), []);

  // Safari fallback: a previous signInWithRedirect resolves on next mount.
  // If we land on the signup page already authed via a provider, advance.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await consumePendingRedirectResult();
        if (cancelled || !result?.user) return;
        // Provider sign-in completed via redirect. Skip ENTRY, go collect phone.
        setMethod(result.providerId?.includes('apple') ? 'apple' : 'google');
        seedProfileFromUser(result.user);
        setStep(STEP.PHONE);
      } catch (err) {
        if (cancelled) return;
        setError(translateAuthError(err));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Google Places autocomplete (PlaceAutocompleteElement, Places API New) — only on
  // the DETAILS step, so the container is mounted before we append the element.
  useEffect(() => {
    if (step !== STEP.DETAILS || !GOOGLE_MAPS_KEY) return;
    let cancelled = false;
    const container = addressContainerRef.current;
    (async () => {
      try {
        await loadMapsScript();
        const { PlaceAutocompleteElement } = await window.google.maps.importLibrary('places');
        if (cancelled || !container) return;
        container.innerHTML = '';
        const el = new PlaceAutocompleteElement({ includedRegionCodes: ['us'] });
        el.style.width = '100%';
        container.appendChild(el);
        el.addEventListener('gmp-select', async ({ placePrediction }) => {
          const place = placePrediction.toPlace();
          await place.fetchFields({ fields: ['formattedAddress'] });
          if (cancelled) return;
          setAddress(place.formattedAddress || '');
        });
      } catch (e) {
        console.error('Places autocomplete failed to load', e);
      }
    })();
    return () => {
      cancelled = true;
      if (container) container.innerHTML = '';
    };
  }, [step]);

  // Cleanup reCAPTCHA on unmount so a fresh widget renders next mount.
  useEffect(() => () => resetRecaptcha(), []);

  function seedProfileFromUser(u) {
    if (!u) return;
    const display = u.displayName || '';
    if (display && !firstName && !lastName) {
      const parts = display.split(' ').filter(Boolean);
      setFirstName(parts[0] || '');
      setLastName(parts.slice(1).join(' '));
    }
    if (u.email && !email) setEmail(u.email);
  }

  // ── Entry: pick provider ────────────────────────────────────────────────
  const handleProvider = async (which) => {
    setError('');
    setProviderLoading(which);
    try {
      if (which === 'email') {
        setMethod('email');
        setStep(STEP.PHONE);
        setProviderLoading(null);
        return;
      }
      const fn = which === 'google' ? signInWithGoogle : signInWithApple;
      const result = await fn();
      // Safari redirect: result is null, completion handled by the redirect
      // effect on next mount.
      if (!result?.user) return;
      setMethod(which);
      seedProfileFromUser(result.user);
      setStep(STEP.PHONE);
    } catch (err) {
      setError(translateAuthError(err));
    } finally {
      setProviderLoading(null);
    }
  };

  // ── Phone: lookup + Firebase OTP send ───────────────────────────────────
  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!phoneIsValid || !phoneE164) {
      setError('Please enter a valid phone number.');
      return;
    }
    setLoading(true);
    try {
      const lookup = await api.lookupPhone(phoneE164);
      if (lookup?.realUserExists) {
        setAccountExists(true);
        setMaskedPhone(lookup.maskedPhone || '');
        setLoading(false);
        return;
      }
      // REMOVED-CRM-ROUTES (2026-08-23) — the CRM shadow-claim path is
      // disabled with the rest of the CRM: a phone matching an imported CRM
      // shadow row now signs up as a plain NEW account (also stops officials/
      // coaches being silently converted to 'client' with the chooser hidden).
      // Restore this block to re-enable.
      // if (lookup?.shadowFound) {
      //   setShadowUserId(lookup.shadowUserId);
      //   setMaskedPhone(lookup.maskedPhone || '');
      // }
      // Send the SMS via Firebase Phone Auth (invisible reCAPTCHA).
      const conf = await startPhoneVerification(phoneE164, 'recaptcha-container');
      setConfirmation(conf);
      setStep(STEP.CODE);
    } catch (err) {
      setError(translateAuthError(err) || 'Could not send code. Try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Code: confirm + link credential to current Firebase user ────────────
  const handleCodeSubmit = async (codeToVerify) => {
    if (!confirmation) {
      setError('Verification session expired. Resend the code.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      if (method === 'email') {
        // For email path the Firebase user doesn't exist yet; the password
        // form on the DETAILS step creates it, then we link the credential.
        // Stash the verification id/code so DETAILS can finish linking.
        // We can confirm the code here directly to surface invalid-code
        // errors early — but confirmationResult.confirm signs in with the
        // PHONE credential, which would prevent createUserWithEmailAndPassword
        // from working (the phone-only user is already signed in). So we
        // defer the actual confirm/link to the DETAILS submit, and only
        // sanity-check digits here.
        if (!/^\d{6}$/.test(codeToVerify)) {
          setError('Enter the 6-digit code.');
          return;
        }
        setCode(codeToVerify);
        setStep(STEP.DETAILS);
        return;
      }

      // Provider paths: a Firebase user already exists. Link the phone.
      await linkPhoneToCurrentUser(confirmation, codeToVerify);
      setCode(codeToVerify);

      if (shadowUserId) {
        // Claim the shadow row — Phase 4a Lambda trusts the Firebase phone
        // identity on the Authorization header (no Twilio code needed). Use
        // authedFetch so the Bearer token actually goes through; api.claimShadow
        // routes through publicFetchJSON which strips Authorization.
        await authedFetch('/auth/verify-claim', {
          method: 'POST',
          body: {
            mode: 'claim',
            shadowUserId,
            firebaseUid: auth.currentUser.uid,
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            email: email.trim(),
            phoneNumber: phoneE164,
            dob,
          },
        });
        await refreshUserInfo();
        await refreshRole();
        setSuccess('Account linked! Taking you in…');
        navTimerRef.current = setTimeout(() => nav('/', { replace: true }), 700);
        return;
      }
      // No shadow — collect the rest of the profile.
      setStep(STEP.DETAILS);
    } catch (err) {
      setError(translateAuthError(err) || 'Verification failed. Try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Details: finish account creation ────────────────────────────────────
  const handleDetailsSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (dobUnder18) { setError('You must be at least 18 years old.'); return; }
    if (method === 'email' && !passwordValid) {
      setError('Password must be 8+ characters with uppercase, lowercase, and a number.');
      return;
    }
    setLoading(true);
    try {
      if (method === 'email') {
        // 1) Create Firebase user from email/password
        try {
          await createUserWithEmailAndPassword(auth, email.trim(), password);
        } catch (err) {
          if ((err?.code || '').includes('email-already-in-use')) {
            setError('An account already exists for this email. Sign in instead.');
            setLoading(false);
            return;
          }
          throw err;
        }
        // 2) Link the verified phone credential to the new user
        if (!confirmation || !code) {
          setError('Phone verification session expired. Restart signup.');
          setLoading(false);
          return;
        }
        await linkPhoneToCurrentUser(confirmation, code);
      }

      const uid = auth.currentUser?.uid;
      if (!uid) throw new Error('Could not determine user ID');

      // 3) Backend write — Phase 4a Lambda accepts the Firebase phone identity
      //    so we don't need a PhoneVerifications row.
      await authedFetch('/users', {
        method: 'POST',
        body: {
          userId: uid,
          email: email.trim(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          phone: phoneE164,
          phoneNumber: phoneE164,
          address: address.trim(),
          dob: dob || undefined,
          role: 'user',
          // Shadow claims are business clients by definition — never official.
          // (Belt-and-suspenders: the chooser is hidden when shadowUserId is set.)
          accountType: shadowUserId ? 'client' : accountType,
        },
      }).catch((err) => {
        const msg = String(err?.message || '').toLowerCase();
        if (msg.includes('already exists')) return; // idempotent retry — fine
        throw err;
      });

      await refreshUserInfo();
      await refreshRole();
      setSuccess('Account created! Taking you in…');
      navTimerRef.current = setTimeout(() => nav('/', { replace: true }), 700);
    } catch (err) {
      setError(translateAuthError(err) || 'Signup failed. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const onResendCode = async () => {
    setError('');
    setCode('');
    setConfirmation(null);
    resetRecaptcha();
    setStep(STEP.PHONE);
  };

  // Auto-verify code once 6 digits entered.
  const lastCodeAttempt = useRef('');
  useEffect(() => {
    if (step !== STEP.CODE) return;
    if (code.length !== 6) return;
    if (loading) return;
    if (lastCodeAttempt.current === code) return;
    lastCodeAttempt.current = code;
    handleCodeSubmit(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, step]);

  return (
    <div className="signup-bg" style={{ backgroundColor: bgColor }}>
      <div className="auth-wordwall" aria-hidden="true">{WORDWALL_TEXT}</div>
      <a href="https://polyp.me" className="signup-brand"><em>Polyp.</em></a>

      <div className="signup-card">
        {step === STEP.PHONE && (
          <button
            type="button"
            className="signup-back-arrow"
            aria-label="Back"
            onClick={() => { setStep(STEP.ENTRY); setMethod(null); }}
            disabled={loading}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
        )}
        <h1 className="signup-title">Sign Up</h1>
        <p className="sub">
          Create your account. To get help onboarding your business, email{' '}
          <a href="mailto:help@polyp.me" className="sub-link">help@polyp.me</a>
        </p>

        {/* Invisible reCAPTCHA host — Firebase mounts here on demand. */}
        <div id="recaptcha-container" />

        {step === STEP.ENTRY && (
          <div className="form">
            <div className="provider-buttons">
              <button
                type="button"
                className="btn provider google"
                onClick={() => handleProvider('google')}
                disabled={!!providerLoading}
              >
                <GoogleIcon />
                <span>{providerLoading === 'google' ? 'Signing in…' : 'Continue with Google'}</span>
              </button>
              <button
                type="button"
                className="btn provider apple"
                onClick={() => handleProvider('apple')}
                disabled={!!providerLoading}
              >
                <AppleIcon />
                <span>{providerLoading === 'apple' ? 'Signing in…' : 'Continue with Apple'}</span>
              </button>
              <button
                type="button"
                className="btn primary"
                onClick={() => handleProvider('email')}
                disabled={!!providerLoading}
              >
                Continue with email
              </button>
            </div>

            {error && <div className="alert error">{error}</div>}
          </div>
        )}

        {step === STEP.PHONE && (
          <form onSubmit={handlePhoneSubmit} className="form">
            <p className="step-prompt">
              Enter your phone number to verify it. We'll send a 6-digit code.
            </p>
            <label className="field">
              <input
                type="tel"
                placeholder="(555) 123-4567"
                autoComplete="tel"
                inputMode="tel"
                value={phone}
                onChange={(e) => {
                  setPhone(formatPhone(e.target.value));
                  if (accountExists) setAccountExists(false);
                }}
                required
                autoFocus
              />
            </label>

            {accountExists && (
              <div className="alert error account-exists-alert">
                <span>This phone is already a Polyp account{maskedPhone ? ` (${maskedPhone})` : ''}. Sign in with your existing method — then you can link Google or Apple from Settings.</span>
                <Link to="/login" className="btn primary">Sign In</Link>
              </div>
            )}
            {error && <div className="alert error">{error}</div>}

            <p className="recaptcha-disclaimer">
              This site is protected by reCAPTCHA and the Google{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>{' '}and{' '}
              <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer">Terms of Service</a>{' '}apply.
            </p>

            <button
              className="btn primary"
              disabled={loading || !phoneIsValid || accountExists}
            >
              {loading ? 'Sending code…' : 'Send code'}
            </button>
          </form>
        )}

        {step === STEP.CODE && (
          <form onSubmit={(e) => e.preventDefault()} className="form">
            <p className="step-prompt">
              Enter the 6-digit code we just sent{maskedPhone ? ` to ${maskedPhone}` : ` to ${phoneDisplay}`}.
            </p>
            <label className="field">
              <input
                type="text"
                className="code-input"
                placeholder="000000"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => {
                  const next = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setCode(next);
                  if (next.length < 6) setError('');
                }}
                maxLength={6}
                required
                autoFocus
                disabled={loading}
              />
            </label>

            {loading && <div className="alert info">Verifying…</div>}
            {error && <div className="alert error">{error}</div>}
            {success && <div className="alert success">{success}</div>}

            <button type="button" className="btn ghost" onClick={onResendCode} disabled={loading}>
              Resend code
            </button>
          </form>
        )}

        {step === STEP.DETAILS && (
          <form onSubmit={handleDetailsSubmit} className="form">
            {shadowUserId && (
              <div className="alert info">
                Your phone is on file with a business. Fill in or correct your details, then finish to link your account.
              </div>
            )}

            {/* No account-type picker since the unified PW Manager
                (2026-09-24): League / Coach / Official are one account, and
                what it can do comes from what it's connected to (an invite, a
                team it creates, a league the Polyp team sets up). */}
            <div className="row two">
              <label className="field">
                <input type="text" placeholder="First name" autoComplete="given-name"
                  value={firstName} onChange={e => setFirstName(e.target.value)} required />
              </label>
              <label className="field">
                <input type="text" placeholder="Last name" autoComplete="family-name"
                  value={lastName} onChange={e => setLastName(e.target.value)} required />
              </label>
            </div>

            <div className="row two">
              <label className="field">
                <input type="email" placeholder="your@email.com" autoComplete="email"
                  value={email} onChange={e => setEmail(e.target.value)}
                  required readOnly={method !== 'email' && !!email} />
              </label>
              {method === 'email' ? (
                <div className="password-group">
                  <label className="field field-password">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Create a password"
                      autoComplete="new-password"
                      minLength={8}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      required
                    />
                    {password && (
                      <button type="button" className="eye-toggle" onClick={() => setShowPassword(v => !v)} tabIndex={-1} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                        {showPassword ? (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                        ) : (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                        )}
                      </button>
                    )}
                  </label>
                  {password && (
                    <div className="pw-checks">
                      <span className={passwordChecks.length ? 'pass' : ''}>8+ chars</span>
                      <span className={passwordChecks.upper ? 'pass' : ''}>Uppercase</span>
                      <span className={passwordChecks.lower ? 'pass' : ''}>Lowercase</span>
                      <span className={passwordChecks.number ? 'pass' : ''}>Number</span>
                    </div>
                  )}
                </div>
              ) : (
                <label className="field is-locked">
                  <input type="text" value={phoneDisplay} readOnly />
                </label>
              )}
            </div>

            <div className="row two">
              {method === 'email' ? (
                <label className="field is-locked">
                  <input type="text" value={phoneDisplay} readOnly />
                </label>
              ) : (
                <span />
              )}
              <label className="field">
                <input
                  type="date"
                  placeholder="Date of birth"
                  autoComplete="bday"
                  value={dob}
                  onChange={e => setDob(e.target.value)}
                  required
                />
                {dobUnder18 && (
                  <span className="field-error">You must be at least 18 years old.</span>
                )}
              </label>
            </div>

            <label className="field">
              <div className="address-autocomplete" ref={addressContainerRef} />
            </label>
            <p className="field-hint">Your address helps us find businesses near you.</p>

            <p className="legal">
              By creating an account, you agree to our{' '}
              <a href="/terms" target="_blank" rel="noreferrer">Terms</a> and{' '}
              <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.
            </p>

            {error && <div className="alert error">{error}</div>}
            {success && <div className="alert success">{success}</div>}

            <button className="btn primary" disabled={loading || dobUnder18}>
              {loading ? 'Creating…' : 'Create My Account'}
            </button>
          </form>
        )}

        <div className="divider"><span>Already have an account?</span></div>
        <Link to="/login" className="btn ghost">Sign In</Link>
      </div>
    </div>
  );
}

function translateAuthError(err) {
  const code = err?.code || '';
  if (code === 'auth/popup-closed-by-user') return 'Sign-in cancelled.';
  if (code === 'auth/popup-blocked') return 'Pop-up was blocked. Allow pop-ups and try again.';
  if (code === 'auth/invalid-verification-code') return 'Invalid code. Try again.';
  if (code === 'auth/code-expired') return 'Code expired. Request a new one.';
  if (code === 'auth/too-many-requests') return 'Too many attempts. Wait a few minutes and try again.';
  if (code === 'auth/credential-already-in-use') {
    return 'This phone is linked to another account. Sign in there and link this method from Settings.';
  }
  if (code === 'auth/account-exists-with-different-credential') {
    return 'An account already exists with this email but a different sign-in method. Sign in with that method, then link this one in Settings.';
  }
  if (code === 'auth/operation-not-supported-in-this-environment') {
    return 'Sign-in not supported in this browser. Try a different browser.';
  }
  if (code === 'auth/network-request-failed') return 'Network error. Check your connection and try again.';
  return err?.message || '';
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#EA4335" d="M9 3.48c1.69 0 2.83.73 3.48 1.34l2.54-2.48C13.46 1.08 11.43 0 9 0 5.48 0 2.44 2.02.96 4.96l2.91 2.26C4.6 5.05 6.62 3.48 9 3.48z"/>
      <path fill="#4285F4" d="M17.64 9.2c0-.74-.06-1.28-.19-1.84H9v3.34h4.96c-.1.83-.64 2.08-1.84 2.92l2.84 2.2c1.7-1.57 2.68-3.88 2.68-6.62z"/>
      <path fill="#FBBC05" d="M3.88 10.78A5.54 5.54 0 0 1 3.58 9c0-.62.11-1.22.29-1.78L.96 4.96A9 9 0 0 0 0 9c0 1.45.35 2.82.96 4.04l2.92-2.26z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.84-2.2c-.76.53-1.78.9-3.12.9-2.38 0-4.4-1.57-5.12-3.74L.97 13.04C2.45 15.98 5.48 18 9 18z"/>
    </svg>
  );
}

function AppleIcon() {
  // Fits cleanly in 0 0 16 16 — leaf at y≈0–3, body at y≈4–15 — so the
  // 18×18 render has no clipping at top or bottom. HIG-compliant.
  return (
    <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M11.182.008C11.148-.03 9.923.023 8.857 1.18c-1.066 1.156-.902 2.482-.878 2.516.024.034 1.52.087 2.475-1.258.955-1.345.762-2.391.728-2.43Zm3.314 11.733c-.048-.096-2.325-1.234-2.113-3.422.212-2.189 1.675-2.789 1.698-2.854.023-.065-.597-.79-1.254-1.157a3.692 3.692 0 0 0-1.563-.434c-.108-.003-.483-.095-1.254.116-.508.139-1.653.589-1.968.607-.316.018-1.256-.522-2.267-.665-.647-.125-1.333.131-1.824.328-.49.196-1.422.754-2.074 2.237-.652 1.482-.311 3.83-.067 4.56.244.729.625 1.924 1.273 2.796.576.984 1.34 1.667 1.659 1.899.319.232 1.219.386 1.843.067.502-.308 1.408-.485 1.766-.472.357.013 1.061.154 1.782.539.571.197 1.111.115 1.652-.105.541-.221 1.324-1.059 2.238-2.758.347-.79.505-1.217.473-1.282Z"/>
    </svg>
  );
}
